import type { RenderTarget as ThreeRenderTarget } from "three/webgpu";
import type { Color } from "../common/color.js";
import { readFrame, readFrameDataUrl, readFramePng, type CaptureOptions, type CapturedFrame } from "./capture.js";
import { GpuContext, type CanvasSize, type GpuContextOptions } from "./GpuContext.js";
import { ImageStore } from "./ImageStore.js";
import { InputRouter } from "./InputRouter.js";
import { Loop } from "./Loop.js";

/**
 * Something drawn every frame: a scene, a HUD, the windowing UI. Layers draw in ascending `order`, each on
 * top of the last, into one frame.
 */
export interface GameLayer {
  /** Ascending. The game's own scenes sit low (0 by convention), front-ends high (the UI is 1000). */
  readonly order: number;
  /** Once per display frame, before anything is decided, with the frame clock in milliseconds. */
  beginFrame?(now: number): void;
  /** True when the layer has something new to show. A game that is not `continuous` paints only then. */
  needsFrame?(): boolean;
  /**
   * Off-screen work, before anything reaches the screen: a scene rendered into a target, a UI's paint walk
   * (whose viewports render their scenes as it goes).
   */
  prepare?(): void;
  /** Draw onto whatever the renderer is pointed at - the canvas, or a capture target. */
  draw(): void;
}

export interface GameOptions {
  gpu?: GpuContextOptions;
  /** Fixed simulation steps per second. 60 by default. */
  hz?: number;
  /** Frames per second at most; 0, the default, is every display frame. */
  maxFps?: number;
  /** The color each frame starts from. Transparent black by default; null to keep the last frame. */
  clear?: Color | null;
  /**
   * Draw every display frame. True by default: a game moves. A purely windowed application sets it false
   * and paints only when a layer asks.
   */
  continuous?: boolean;
  /** Start the loop at once. True by default. */
  autoStart?: boolean;
}

const TRANSPARENT: Color = { r: 0, g: 0, b: 0, a: 0 };

/**
 * The game: the canvas and its renderer, the loop, the input, the assets and the layers drawn each frame.
 *
 * Everything else is built on it. A game adds its scenes as layers and listens on `input` for what no
 * front-end claimed; the windowing UI (`webwin/ui`'s `Desktop`) is one more layer, drawn over the game and
 * offered input first.
 *
 * ```ts
 * const game = await Game.create(canvas);
 * game.onUpdate((dt) => world.step(dt));
 * game.input.on("keydown", (e) => { if (e.code === "Space") player.jump(); });
 * ```
 */
export class Game {
  readonly gpu: GpuContext;
  readonly images: ImageStore;
  readonly input: InputRouter;
  readonly loop: Loop;

  /** See `GameOptions.clear`. */
  clear: Color | null;
  /** See `GameOptions.continuous`. */
  continuous: boolean;

  #layers: GameLayer[] = [];
  #updates = new Set<(dt: number) => void>();
  #resizes = new Set<(size: CanvasSize) => void>();
  #frameRequested = true;
  #disposed = false;

  constructor(gpu: GpuContext, options: GameOptions = {}) {
    this.gpu = gpu;
    this.images = new ImageStore(gpu);
    this.input = new InputRouter(gpu.canvas);
    this.clear = options.clear === undefined ? TRANSPARENT : options.clear;
    this.continuous = options.continuous ?? true;
    this.loop = new Loop({
      hz: options.hz,
      maxFps: options.maxFps,
      update: (dt) => {
        for (const update of [...this.#updates]) update(dt);
      },
      render: () => this.#frame(),
    });
  }

  static async create(canvas: HTMLCanvasElement, options: GameOptions = {}): Promise<Game> {
    let game: Game | null = null;
    const gpu = await GpuContext.create(canvas, {
      ...options.gpu,
      onResize: (size) => {
        if (game) {
          for (const resized of [...game.#resizes]) resized(size);
          game.requestFrame();
        }
        options.gpu?.onResize?.(size);
      },
    });
    game = new Game(gpu, options);
    if (options.autoStart ?? true) game.start();
    return game;
  }

  get canvas(): HTMLCanvasElement {
    return this.gpu.canvas;
  }

  get size(): CanvasSize {
    return this.gpu.size;
  }

  /** Add a layer; returns a function that removes it. */
  addLayer(layer: GameLayer): () => void {
    this.#layers.push(layer);
    // Stable: layers of equal order keep the order they were added in.
    this.#layers.sort((a, b) => a.order - b.order);
    this.requestFrame();
    return () => this.removeLayer(layer);
  }

  removeLayer(layer: GameLayer): void {
    const i = this.#layers.indexOf(layer);
    if (i >= 0) this.#layers.splice(i, 1);
    this.requestFrame();
  }

  get layers(): readonly GameLayer[] {
    return this.#layers;
  }

  /** Run `update` every fixed step with the step length in seconds. Returns a function that stops it. */
  onUpdate(update: (dt: number) => void): () => void {
    this.#updates.add(update);
    return () => this.#updates.delete(update);
  }

  /** Called after the canvas's backing store changes size. Returns a function that stops it. */
  onResize(resized: (size: CanvasSize) => void): () => void {
    this.#resizes.add(resized);
    return () => this.#resizes.delete(resized);
  }

  /** Draw the next display frame even if nothing says it changed. */
  requestFrame(): void {
    this.#frameRequested = true;
  }

  start(): void {
    if (!this.#disposed) this.loop.start();
  }

  stop(): void {
    this.loop.stop();
  }

  #frame(): void {
    const now = performance.now();
    for (const layer of this.#layers) layer.beginFrame?.(now);
    let due = this.continuous || this.#frameRequested;
    for (const layer of this.#layers) if (!due && layer.needsFrame?.()) due = true;
    if (due) this.render();
  }

  /**
   * Draw one frame now: every layer's off-screen work, then every layer onto `target` - the canvas when
   * omitted.
   */
  render(target: ThreeRenderTarget | null = null): void {
    if (this.#disposed) return;
    this.#frameRequested = false;
    this.gpu.syncSize();
    const layers = [...this.#layers];
    for (const layer of layers) layer.prepare?.();
    this.gpu.beginFrame(this.clear ?? null, target);
    for (const layer of layers) {
      layer.draw();
      // A layer may point the renderer elsewhere while it draws; the next one draws on the frame again.
      this.gpu.renderer.setRenderTarget(target);
    }
  }

  /**
   * Draw one frame into a target and read it back - a screenshot of exactly what the GPU drew, of a frame
   * you chose, on any platform.
   */
  capture(options?: CaptureOptions): Promise<Blob> {
    return readFramePng(this.gpu, this.#renderCapture(), options);
  }

  /** The same capture as a `data:image/png;base64,…` URL - the form that survives `page.evaluate`. */
  captureDataUrl(options?: CaptureOptions): Promise<string> {
    return readFrameDataUrl(this.gpu, this.#renderCapture(), options);
  }

  /** The same capture as raw RGBA8 pixels, for a golden-image comparison that wants no encoder. */
  capturePixels(options?: CaptureOptions): Promise<CapturedFrame> {
    return readFrame(this.gpu, this.#renderCapture(), options);
  }

  #renderCapture(): ThreeRenderTarget {
    const target = this.gpu.captureTarget();
    this.render(target);
    this.gpu.renderer.setRenderTarget(null);
    return target;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.stop();
    this.#layers.length = 0;
    this.#updates.clear();
    this.input.dispose();
    this.images.clear();
    this.gpu.dispose();
  }
}
